/**
 * In-app how-to guide for the marketing team. Content is meant to
 * outlive the current UI details — descriptions of the workflow, the
 * data model, the callouts to watch out for — so it doesn't need
 * updating every time a button label changes.
 *
 * All styles live in a scoped .how-to-guide class so nothing leaks
 * into the rest of mosy.
 */
import { useEffect } from 'react';

const STYLES = `
.how-to-guide {
  --hg-paper: #fbfbfa;
  --hg-bone: #edece5;
  --hg-ink: #2b4138;
  --hg-ink-700: #3d5448;
  --hg-ink-500: #6d8276;
  --hg-ink-300: #a8b6ad;
  --hg-yellow: #ffbd09;
  --hg-yellow-tint: #fff6d9;
  --hg-border: rgba(43,65,56,0.12);
  --hg-border-strong: rgba(43,65,56,0.2);
  --hg-danger: #b54338;
  --hg-danger-tint: #fde7e5;
  --hg-font: "TWK Lausanne", "Inter", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  --hg-mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;

  background: var(--hg-paper);
  color: var(--hg-ink);
  font-family: var(--hg-font);
  border-radius: 12px;
  border: 1px solid var(--hg-border);
  overflow: hidden;
}

.how-to-guide .hg-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 48px;
  max-width: 1080px;
  margin: 0 auto;
  padding: 40px 40px 72px;
}

.how-to-guide .hg-main { min-width: 0; max-width: 720px; }

.how-to-guide .hg-toc {
  position: sticky;
  top: 12px;
  align-self: start;
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  padding-left: 20px;
  border-left: 1px solid var(--hg-border);
  font-size: 12.5px;
}
.how-to-guide .hg-toc-eyebrow {
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--hg-ink-500);
  font-weight: 700;
  margin: 22px 0 8px;
  padding-top: 20px;
  border-top: 1px solid var(--hg-border);
}
.how-to-guide .hg-toc-eyebrow:first-of-type {
  border-top: none; padding-top: 0; margin-top: 0;
}
.how-to-guide .hg-toc-list {
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 3px;
}
.how-to-guide .hg-toc-list a {
  display: flex; align-items: baseline; gap: 10px;
  color: var(--hg-ink-500); text-decoration: none;
  padding: 3px 0; line-height: 1.4;
}
.how-to-guide .hg-toc-list a:hover { color: var(--hg-ink); }
.how-to-guide .hg-toc-list .num {
  font-family: var(--hg-mono);
  font-size: 10.5px;
  color: var(--hg-ink-300);
  letter-spacing: 0.04em;
  flex-shrink: 0; width: 20px;
  font-variant-numeric: tabular-nums;
}

.how-to-guide .hg-hero {
  padding: 8px 0 32px;
  border-bottom: 1px solid var(--hg-border);
  margin-bottom: 40px;
}
.how-to-guide .hg-hero-eyebrow {
  font-size: 10.5px; letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--hg-yellow);
  font-weight: 700; margin-bottom: 12px;
}
.how-to-guide .hg-hero h1 {
  font-size: 44px; line-height: 1.03;
  letter-spacing: -0.03em; font-weight: 300;
  margin: 0 0 12px;
  text-wrap: balance;
}
.how-to-guide .hg-hero p {
  font-size: 16px; color: var(--hg-ink-500);
  max-width: 60ch; margin: 0; line-height: 1.55;
}

.how-to-guide .hg-quickstart {
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 12px 14px;
  padding: 22px 26px;
  background: var(--hg-bone);
  border-radius: 12px;
  margin: 32px 0 48px;
  align-items: baseline;
}
.how-to-guide .hg-quickstart .hg-qs-eyebrow {
  grid-column: 1 / -1;
  font-size: 10.5px; letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--hg-ink-500);
  font-weight: 700; margin-bottom: 4px;
}
.how-to-guide .hg-quickstart .hg-qs-num {
  font-family: var(--hg-mono);
  font-size: 11.5px;
  color: var(--hg-ink-500);
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.how-to-guide .hg-quickstart .hg-qs-step {
  font-size: 14px; line-height: 1.5;
  color: var(--hg-ink);
}
.how-to-guide .hg-quickstart .hg-qs-step strong { font-weight: 600; }

.how-to-guide section {
  scroll-margin-top: 20px;
  padding-top: 36px;
}
.how-to-guide section + section {
  border-top: 1px solid var(--hg-border);
}
.how-to-guide .hg-section-head {
  display: flex; align-items: baseline; gap: 18px;
  margin-bottom: 6px;
}
.how-to-guide .hg-section-num {
  font-family: var(--hg-mono);
  font-size: 11.5px; color: var(--hg-ink-500);
  font-variant-numeric: tabular-nums;
  padding-top: 8px;
}
.how-to-guide section h2 {
  font-size: 30px; line-height: 1.1;
  letter-spacing: -0.022em; font-weight: 300;
  margin: 0; text-wrap: balance;
  color: var(--hg-ink);
}
.how-to-guide .hg-lede {
  font-size: 15px; color: var(--hg-ink-500);
  margin: 4px 0 24px; max-width: 60ch;
  line-height: 1.55;
}
.how-to-guide h3 {
  font-size: 17.5px; font-weight: 500;
  letter-spacing: -0.01em;
  margin: 26px 0 8px;
  color: var(--hg-ink);
}
.how-to-guide p {
  margin: 0 0 12px;
  font-size: 15px; line-height: 1.65;
  color: var(--hg-ink-700);
}
.how-to-guide ul, .how-to-guide ol {
  margin: 0 0 14px; padding-left: 18px;
  color: var(--hg-ink-700);
  font-size: 15px; line-height: 1.65;
}
.how-to-guide li + li { margin-top: 3px; }
.how-to-guide strong { color: var(--hg-ink); font-weight: 600; }
.how-to-guide em { font-style: italic; color: var(--hg-ink); }
.how-to-guide code {
  font-family: var(--hg-mono);
  font-size: 0.88em;
  padding: 1px 6px;
  background: var(--hg-bone);
  border-radius: 4px;
  color: var(--hg-ink);
}
.how-to-guide a {
  color: var(--hg-ink);
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
}

.how-to-guide .hg-callout {
  padding: 14px 18px;
  border-radius: 10px;
  margin: 20px 0;
  font-size: 14px; line-height: 1.55;
  color: var(--hg-ink);
  border: 1px solid var(--hg-border);
}
.how-to-guide .hg-callout .hg-cl-label {
  display: block;
  font-size: 10px; letter-spacing: 0.18em;
  text-transform: uppercase; font-weight: 700;
  margin-bottom: 4px;
}
.how-to-guide .hg-callout.tip {
  background: var(--hg-yellow-tint);
  border-color: color-mix(in srgb, var(--hg-yellow) 45%, transparent);
}
.how-to-guide .hg-callout.tip .hg-cl-label {
  color: color-mix(in srgb, var(--hg-yellow) 30%, var(--hg-ink));
}
.how-to-guide .hg-callout.warn {
  background: var(--hg-danger-tint);
  border-color: color-mix(in srgb, var(--hg-danger) 30%, transparent);
}
.how-to-guide .hg-callout.warn .hg-cl-label {
  color: var(--hg-danger);
}
.how-to-guide .hg-callout.once {
  background: var(--hg-bone);
  border-color: var(--hg-border);
}
.how-to-guide .hg-callout.once .hg-cl-label {
  color: var(--hg-ink-500);
}

.how-to-guide .hg-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 2px 9px; border-radius: 999px;
  font-size: 11.5px; font-weight: 600;
  line-height: 1.4; white-space: nowrap;
  vertical-align: 1px;
}
.how-to-guide .hg-chip .sw {
  width: 7px; height: 7px; border-radius: 50%; display: inline-block;
}
.how-to-guide .hg-chip.pending { background: var(--hg-bone); color: var(--hg-ink-500); }
.how-to-guide .hg-chip.contacted { background: #fff7e0; color: #a35a00; }
.how-to-guide .hg-chip.reminder { background: #efe6fa; color: #5e3a9e; }
.how-to-guide .hg-chip.followup { background: #fde7d3; color: #a04d0a; }
.how-to-guide .hg-chip.replied { background: #e6f0ff; color: #1d4ed8; }
.how-to-guide .hg-chip.confirmed { background: #e7f6ec; color: #0d7a36; }
.how-to-guide .hg-chip.auto { background: #eef1f7; color: #475569; }
.how-to-guide .hg-chip.declined { background: #fbe7e7; color: #9a1d1d; }
.how-to-guide .hg-chip.excluded { background: #ececec; color: #6b7280; }
.how-to-guide .hg-chip.stance-open .sw { background: #1f9d55; }
.how-to-guide .hg-chip.stance-negotiate .sw { background: #d97706; }
.how-to-guide .hg-chip.stance-restricted .sw { background: #c83333; }
.how-to-guide .hg-chip.stance-open { background: #e7f6ec; color: #0d7a36; }
.how-to-guide .hg-chip.stance-negotiate { background: #fff4e0; color: #9a5400; }
.how-to-guide .hg-chip.stance-restricted { background: #fde7e7; color: #8b1d1d; }

.how-to-guide .hg-defs {
  display: grid;
  grid-template-columns: minmax(160px, 200px) 1fr;
  gap: 12px 24px;
  margin: 14px 0 22px;
  padding: 18px 22px;
  background: var(--hg-bone);
  border-radius: 10px;
}
.how-to-guide .hg-defs dt {
  font-weight: 600; color: var(--hg-ink);
  font-size: 14px; line-height: 1.5;
}
.how-to-guide .hg-defs dd {
  margin: 0; color: var(--hg-ink-700);
  font-size: 14px; line-height: 1.55;
}

.how-to-guide .hg-footer {
  margin-top: 60px;
  padding-top: 24px;
  border-top: 1px solid var(--hg-border);
  color: var(--hg-ink-500);
  font-size: 12.5px;
  display: flex; justify-content: space-between;
  align-items: baseline; gap: 16px; flex-wrap: wrap;
}
.how-to-guide .hg-footer .yellow-dot {
  display: inline-block; width: 7px; height: 7px;
  border-radius: 50%; background: var(--hg-yellow);
  margin-right: 8px; vertical-align: 1px;
}

@media (max-width: 900px) {
  .how-to-guide .hg-grid {
    grid-template-columns: 1fr;
    padding: 24px 22px 60px;
    gap: 24px;
  }
  .how-to-guide .hg-toc {
    position: static; max-height: none;
    padding-left: 0; padding-top: 20px;
    border-left: none;
    border-top: 1px solid var(--hg-border);
    order: 2;
  }
  .how-to-guide .hg-main { max-width: none; order: 1; }
  .how-to-guide .hg-hero h1 { font-size: 32px; }
  .how-to-guide section h2 { font-size: 24px; }
  .how-to-guide .hg-defs {
    grid-template-columns: 1fr;
    gap: 4px 0; padding: 16px;
  }
  .how-to-guide .hg-defs dt { margin-top: 6px; }
  .how-to-guide .hg-defs dt:first-of-type { margin-top: 0; }
}

::selection { background: var(--hg-yellow); color: var(--hg-ink); }
`;

const HowToGuide = () => {
  useEffect(() => { document.title = 'Mosy · How to use it'; }, []);

  // When the guide is opened with #<section-id> — the "How it works"
  // links do this from other pages — scroll to that section on mount
  // so the user lands exactly on what they wanted to read.
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace(/^#/, '');
      if (!id) return;
      // Wait a tick so the DOM is committed before we look for the anchor.
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  return (
    <div className="how-to-guide">
      <style>{STYLES}</style>
      <div className="hg-grid">
        <main className="hg-main">
          <div className="hg-hero">
            <div className="hg-hero-eyebrow">Mosy · How to use it</div>
            <h1>Everything the marketing team needs to run a LampTwist campaign.</h1>
            <p>Skim it, bookmark it, hand it to whoever joins the team next. Each section covers one thing you actually do, in the order you usually do it.</p>
          </div>

          <div className="hg-quickstart" aria-label="Quick start">
            <div className="hg-qs-eyebrow">Golden path — from empty campaign to invoice</div>
            <div className="hg-qs-num">01</div>
            <div className="hg-qs-step"><strong>Create the campaign</strong> · type, dates, and its placements (banners, grid, newsletter…).</div>
            <div className="hg-qs-num">02</div>
            <div className="hg-qs-step"><strong>Give placements brand slots</strong> · tick the ones brands can book and set how many brands fit (a product grid = 6, collection cards = 4). Those become the slots for sale.</div>
            <div className="hg-qs-num">03</div>
            <div className="hg-qs-step"><strong>Create the partnership page</strong> · it lists the slots by itself. Check the texts and prices, translate, then <strong>Publish</strong>.</div>
            <div className="hg-qs-num">04</div>
            <div className="hg-qs-step"><strong>Outreach</strong> · add the brands you want to sell to, then click ✉ per brand: the mail carries the brand's personal booking link. Mark <span className="hg-chip contacted">Contacted</span>.</div>
            <div className="hg-qs-num">05</div>
            <div className="hg-qs-step"><strong>Brands book</strong> · they pick their days and placements and sign up. The first to sign up gets the slot; their row turns <span className="hg-chip confirmed">Signed up</span> and they're tagged on the placements.</div>
            <div className="hg-qs-num">06</div>
            <div className="hg-qs-step"><strong>Fill in results</strong> · after the campaign, drop the numbers per placement — rates auto-compute.</div>
            <div className="hg-qs-num">07</div>
            <div className="hg-qs-step"><strong>Invoice</strong> · prepare the invoice from the Billing tab. (Per-brand results come later, once we log results.)</div>
          </div>

          <section id="intro">
            <div className="hg-section-head"><span className="hg-section-num">00</span><h2>What mosy is</h2></div>
            <p className="hg-lede">A single home for the LampTwist marketing calendar — brands, campaigns, the partnerships that pay for them, and everything we produce along the way.</p>
            <p>Everything runs off two ideas: a <strong>brand</strong> (the people we work with) and a <strong>campaign</strong> (a moment in time we're publishing towards). Every other thing in mosy hangs off one of those.</p>
            <ul>
              <li><strong>Placements</strong> — newsletters, IG posts, banners we're going to publish.</li>
              <li><strong>Tasks</strong> — the work that gets a placement ready.</li>
              <li><strong>Partnerships</strong> — the offering a brand can join for a campaign; drives who's in and what we owe them.</li>
              <li><strong>Attachments and notes</strong> — files, briefs, thoughts, tied to whichever brand / campaign / task / placement they belong to.</li>
            </ul>
          </section>

          <section id="getting-around">
            <div className="hg-section-head"><span className="hg-section-num">01</span><h2>Getting around</h2></div>
            <p className="hg-lede">The left sidebar is the whole map. Most days you'll live on Dashboard, My work, Campaigns and Brands.</p>
            <dl className="hg-defs">
              <dt>Dashboard</dt><dd>Home base. What's live this week, what needs attention.</dd>
              <dt>My work</dt><dd>Tasks assigned to you, grouped by campaign. Deadlines first.</dd>
              <dt>Campaigns</dt><dd>List, calendar, and Gantt-style timeline of everything we're running. The one place to pick which campaign you're editing.</dd>
              <dt>Brands</dt><dd>Every partner brand we work with, in alphabetical order, with a search box.</dd>
              <dt>Social · Newsletters · Homepage</dt><dd>Per-channel calendars — one line per placement, drag to reschedule.</dd>
              <dt>Tasks</dt><dd>The full task list across everything, filterable.</dd>
              <dt>Notifications</dt><dd>Anything that pinged you.</dd>
            </dl>
            <p>Top-right, the <strong>search bar</strong> reaches into brands, campaigns, placements and tasks — the fastest way to jump anywhere.</p>
          </section>

          <section id="brands">
            <div className="hg-section-head"><span className="hg-section-num">02</span><h2>Brands</h2></div>
            <p className="hg-lede">The source of truth for every partner we work with. Set them up carefully once — the rest of mosy leans on this data.</p>
            <h3>What lives on a brand</h3>
            <dl className="hg-defs">
              <dt>Priority</dt><dd>A / B / C rating stored on each brand but not shown in the app for now — it will come back once we start using it.</dd>
              <dt>Discount stance</dt>
              <dd>
                Their pricing sensitivity — click the chip in the header to change it. Three values:
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span className="hg-chip stance-open"><span className="sw"></span>Open</span>
                  <span className="hg-chip stance-negotiate"><span className="sw"></span>Negotiate</span>
                  <span className="hg-chip stance-restricted"><span className="sw"></span>Restricted</span>
                </div>
              </dd>
              <dt>Permanent discount</dt><dd>The year-round discount we already run on their products (e.g. <code>10%</code>). When set, it auto-fills the Discount column on their partnership rows, marked <strong>AUTO</strong>. Overriding clears the tag.</dd>
              <dt>Contacts</dt><dd>Up to three per brand. All of them get pre-filled in the To field when we compose an outreach email.</dd>
              <dt>Notes and attachments</dt><dd>Your working scratchpad plus any file we've been sent (briefs, catalogues, contracts).</dd>
              <dt>Visibility plan</dt><dd>Only for brands we've committed year-round slots to. Turn on to open a per-brand tally of newsletters, social posts, homepage moments etc. and how many we've delivered so far.</dd>
            </dl>
            <div className="hg-callout tip">
              <span className="hg-cl-label">Shortcut</span>
              On any brand page, the small arrows next to <em>All brands</em> — or just the ← / → keys on your keyboard — page through brands one at a time. Handy when you're tagging a batch of brands with a new field.
            </div>
            <div className="hg-callout warn">
              <span className="hg-cl-label">Watch out</span>
              Restricted brands show a red banner on the campaign modal the moment you tick them. Only include them if you've already cleared an exception on your side — the warning is there for a reason.
            </div>
          </section>

          <section id="campaigns">
            <div className="hg-section-head"><span className="hg-section-num">03</span><h2>Campaigns</h2></div>
            <p className="hg-lede">A campaign is a moment in time we're publishing towards. Everything else — placements, tasks, partnership — hangs off it.</p>
            <h3>Creating one</h3>
            <ul>
              <li><strong>Type</strong>: Promotion (site-wide moment, discount-driven), Brand (spotlight on one label), Theme (season / trend / editorial).</li>
              <li><strong>Dates</strong>: skip them at first if you're still shaping the idea. Undated campaigns sit in a "To plan" group at the top of the list.</li>
              <li><strong>Nest</strong>: hang a smaller campaign under a bigger one (e.g. a brand push inside a Promotion). Just linkage — no rules.</li>
              <li><strong>Brands</strong>: every brand featured in the campaign, free or paid. Tagging a brand on a placement adds it here automatically; adding it here doesn't put it on a placement. Removing a brand from one never removes it from the other — mosy reminds you where it still is when you save.</li>
            </ul>
            <h3>Three ways to look at them</h3>
            <p>List, Calendar, Timeline. The Timeline (Gantt) is the best for spotting overlap; the Calendar is friendliest at a glance; the List is fastest to filter.</p>
            <h3>Duplicating</h3>
            <p>On any campaign, the <strong>Duplicate</strong> action copies the placements and their tasks to a new campaign, resetting all dates to a new start you pick. Fastest way to bootstrap next year's Summer Sales from this year's.</p>
          </section>

          <section id="placements">
            <div className="hg-section-head"><span className="hg-section-num">04</span><h2>Placements</h2></div>
            <p className="hg-lede">One placement = one thing we publish. A newsletter send, an Instagram post, a homepage banner, a discount, a giveaway. If it goes out, it's a placement.</p>
            <p>Before, we called these "assets". We renamed them because brands use "assets" for their own logos and images — too much confusion. In mosy, a placement is <em>where</em> the brand shows up.</p>
            <h3>What each placement carries</h3>
            <dl className="hg-defs">
              <dt>Category · Type</dt><dd>Email → Newsletter · Social Media → Instagram Post / Story / Reel / Giveaway, Facebook · Homepage → Banner 100% / 50%, Grid, Carousel, etc. · Catalog → PDP banner, Product label, Collection page, Featured products on collection · Promotion → Discount.</dd>
              <dt>Dates</dt><dd>Publish date; end date for placements that run over a range. Blank is fine while you plan.</dd>
              <dt>Status</dt><dd>Not started → In progress → Ready → Done.</dd>
              <dt>Brands</dt><dd>Link every brand featured in this placement. For newsletters, one brand can be marked as the "dedicated" one.</dd>
              <dt>Proof of delivery</dt><dd>After it goes live: paste the live URL and drop a screenshot. Shows up on lookbooks and partner reports.</dd>
              <dt>Metrics</dt><dd>Post-publish numbers per channel (recipients / opens / clicks for newsletters; reach / impressions / engagement for Instagram). Rates compute themselves.</dd>
              <dt>Attachments and tasks</dt><dd>Files tied to this specific placement, and any prep tasks linked to it.</dd>
            </dl>
          </section>

          <section id="tasks">
            <div className="hg-section-head"><span className="hg-section-num">05</span><h2>Tasks</h2></div>
            <p className="hg-lede">The work that gets a placement ready. Or campaign-wide work that isn't tied to a specific placement.</p>
            <ul>
              <li>Assign to one or several teammates — they'll see it under <strong>My work</strong>.</li>
              <li>Link to a campaign (required) and optionally a placement (so it appears in that placement's row).</li>
              <li>Attach files at creation or after.</li>
              <li>Leave comments — the whole team can follow along.</li>
            </ul>
          </section>

          <section id="placements-prices">
            <div className="hg-section-head"><span className="hg-section-num">06</span><h2>Placements &amp; prices</h2></div>
            <p className="hg-lede">Settings → Placements &amp; prices is what we sell, at what price, and where it sits on our website. Set it up once; every campaign reads from it.</p>
            <h3>The words we use</h3>
            <dl className="hg-defs">
              <dt>Placement</dt><dd>One thing we publish on a date, inside a campaign (Monday's hero banner, Tuesday's newsletter).</dd>
              <dt>Slot</dt><dd>A spot for one brand on a placement. A product grid placement with 6 brand slots can carry 6 brands.</dd>
              <dt>Collection cards</dt><dd>The four photo cards under the homepage product grid ("20% off Serax"). Brands book one card per day as "Homepage brand card"; give the placement 4 brand slots.</dd>
              <dt>“1 slot” frame</dt><dd>In Settings → Placement map, tick <em>Shows one slot</em> on a frame drawn around one card or tile inside the big frame. Brands then see exactly what they book. In placement texts, put **…** around words to show them in bold.</dd>
              <dt>Sellable placement</dt><dd>A line on Placements &amp; prices that brands can book — e.g. "Homepage product grid", "Dedicated newsletter", "Social pack".</dd>
              <dt>Sold as</dt><dd>Which line a campaign placement is sold as. Filled in automatically; you only pick it where there's a real choice (a newsletter can be a dedicated newsletter or a feature in one).</dd>
              <dt>Pack</dt><dd>A line that sells several placement types as one item — the Social pack is an Instagram post + story + reel on the same day.</dd>
            </dl>
            <h3>Three tabs</h3>
            <dl className="hg-defs">
              <dt>Placements &amp; prices</dt><dd>Every placement type, grouped by category. Tick <strong>Sellable</strong> for what brands can book, and set the <strong>standard price</strong> of one slot. No price = brands see "On request". Placements that aren't on the website (newsletter, Social pack) get <strong>mockup pictures</strong> here — every campaign shows them unless it has its own.</dd>
              <dt>Placement map</dt><dd>Our website pages (homepage, collection, product page) as a stack of section screenshots, with a frame drawn on every spot we sell. Each frame is linked to a line, so brands see exactly where they'll appear. You can add, reorder, replace and crop sections, and move or resize frames.</dd>
              <dt>Moments calendar</dt><dd>The year's big moments and how many slots each one offers. Not used by the booking page yet.</dd>
            </dl>
            <div className="hg-callout once">
              <span className="hg-cl-label">Do it once</span>
              Prices, mockup pictures and the map are standards. A campaign follows them live — change a standard price here and every campaign without its own price follows straight away.
            </div>
          </section>

          <section id="partnership-setup">
            <div className="hg-section-head"><span className="hg-section-num">07</span><h2>Set up the booking page</h2></div>
            <p className="hg-lede">A campaign's partnership page is a booking page: brands pick dated slots and sign up. Signing up is final, and the first brand to sign up gets the slot.</p>
            <h3>Before you start: brand slots</h3>
            <p>The campaign comes first. On the campaign page, give the placements brands can book a number of <strong>Brand slots</strong> — in the placement itself, or for many at once with the <strong>Brand slots…</strong> shortcut (tick placements, set the number). A placement with at least one slot is for sale; one without is a normal placement.</p>
            <p>Then click <strong>Create partnership page</strong> in the Partnership block on the campaign page. The editor has four tabs: <strong>Page · Placements · Outreach · Billing</strong>. (Old pack pages also have <strong>Results</strong>; on booking pages it comes back once we log real results.)</p>
            <h3>The bar at the top</h3>
            <dl className="hg-defs">
              <dt>Sign-ups</dt><dd>Open or Closed. When closed, the page still shows everything, but brands can't book.</dd>
              <dt>Minimum discount</dt><dd>Optional: the lowest customer discount a brand must give to sign up (e.g. 15%).</dd>
              <dt>Book by</dt><dd>The date we'd like bookings in by. Shown to brands; nothing closes by itself.</dd>
              <dt>Campaign brands on the page</dt><dd>Whether brands see who else takes part.</dd>
              <dt>Reply-to</dt><dd>The teammates on CC of the emails.</dd>
            </dl>
            <h3>Page tab</h3>
            <dl className="hg-defs">
              <dt>Page text</dt><dd>Headline and intro brands read at the top. <strong>Generate with AI</strong> writes a first version from the campaign.</dd>
              <dt>Website pages</dt><dd>This campaign's version of the placement map: hide sections that aren't used, or upload an <strong>own mockup</strong> (e.g. the homepage with the Black Friday design). Paste from the clipboard and crop work here too. <strong>↺ Default</strong> goes back to the standard picture.</dd>
              <dt>Good to know</dt><dd>Practical info, FAQ, and the sign-up terms brands accept when they sign up.</dd>
              <dt>Translations (NL · FR)</dt><dd><strong>Translate with AI</strong> makes the Dutch and French versions. They say "out of date" when you change the English text — translate again before you send the links.</dd>
              <dt>Emails</dt><dd>The outreach email and the confirmation email. Placeholders like <code>{'{brand}'}</code>, <code>{'{link}'}</code>, <code>{'{slots}'}</code>, <code>{'{total}'}</code> are filled in per brand.</dd>
            </dl>
            <h3 id="partnership-placements">Placements tab</h3>
            <p>One card per sellable placement in this campaign, with its slots by date. The list builds itself from the brand slots — you don't add cards by hand.</p>
            <dl className="hg-defs">
              <dt>What partners read</dt><dd>One or two lines about what the brand gets.</dd>
              <dt>What we need from you</dt><dd>What the brand has to deliver (visuals, a reel, a product list…).</dd>
              <dt>Price per slot</dt><dd>Empty = the standard price from Settings. Type a price to change it for this campaign. A single slot can have its own price in the table (e.g. a pricier Black Friday).</dd>
              <dt>Expected per day</dt><dd>Two numbers brands see next to each day's price: page views · sessions (website), recipients · opens (newsletter), reach · impressions (social). Standard in Settings, this campaign's here (e.g. last year's Black Week), one day's own in the slot table (e.g. Black Friday) — so a pricier day explains itself.</dd>
              <dt>Max per brand</dt><dd>The most slots one brand may book of it (e.g. 2 hero days), so nobody takes them all. Empty = the standard from Settings → Placements &amp; prices.</dd>
              <dt>★ Stand out</dt><dd>Highlights a slot on the booking page — for the day you want to push.</dd>
              <dt>For sale / Not for sale</dt><dd>Hide a whole placement from the booking page without touching the campaign.</dd>
              <dt>Where brands see it</dt><dd>Its frame on the website pages, or — for a newsletter or social — the mockup pictures (standard ones from Settings, or upload this campaign's own).</dd>
            </dl>
            <div className="hg-callout tip">
              <span className="hg-cl-label">Going live</span>
              The page stays a <strong>Draft</strong> until you click <strong>Publish</strong> at the top of the editor — before that, the brand links don't open. Translate first, then publish.
            </div>
            <div className="hg-callout warn">
              <span className="hg-cl-label">Watch out</span>
              A card that says the slots "don't say what brands book yet" needs a <strong>Sold as</strong> choice — pick it right there on the card, or brands won't see those slots.
            </div>
          </section>

          <section id="outreach">
            <div className="hg-section-head"><span className="hg-section-num">08</span><h2>Reach out</h2></div>
            <p className="hg-lede">The Outreach tab is where you actually run the campaign. One row per brand, one status per brand.</p>
            <h3>Who's on it</h3>
            <p>On a booking page, Outreach is <strong>its own list: only the brands you want to sell to</strong>. Add them with <strong>+ Add brand to reach out to…</strong>, or <strong>Add all campaign brands</strong> when you do want to email everyone. Featuring a brand on a placement (a free newsletter, a social post, an unbooked slot you fill) puts it in the campaign, not here — so it gets no booking link and no invoice. A brand that books joins the campaign and its placements by itself.</p>
            <dl className="hg-defs">
              <dt>Campaign brands</dt><dd>Every brand featured, free or paid.</dd>
              <dt>Placement brands</dt><dd>Who appears in that one placement, free or paid.</dd>
              <dt>Outreach</dt><dd>Only the brands we sell to: personal link, sign-up, confirmation email, invoice.</dd>
            </dl>
            <p>Once a brand books, its row shows <strong>Booked</strong> (each placement, day and price), <strong>Signed up on</strong> (date and time — first to sign up gets the slot) and the <strong>Total</strong>. The top line counts the booked slots and euros. The green ✓ confirmation email only appears for brands that signed up. <strong>A brand calls to change something?</strong> Click × on a booked slot to take it out (the slot is free again), or <strong>+ Add slot</strong> to book a free day for them at today's price; the Total, Billing and the confirmation email follow. There are no packs or add-ons on a booking page, and the Discount column only shows when the page asks for a minimum discount.</p>
            <p><strong>Want to suggest a selection to a brand?</strong> Click <strong>+ Prepare selection</strong> on its row and pick the slots you'd propose; they show as dashed chips with the proposed total. When the brand opens its own link, those slots are already ticked with a short note at the top, so it only has to review and sign up (it can still change them). A proposal doesn't reserve anything: if another brand books one of the slots first, it's crossed out on the row and the brand's page says it's no longer available. Once the brand signs up, the proposal disappears and its real booking takes over.</p>
            <p>Old pack pages (e.g. Scandinavian Days) work as before: every campaign brand is on Outreach.</p>
            <h3>The status flow</h3>
            <p>
              <span className="hg-chip pending">To contact</span>{' '}
              <span className="hg-chip contacted">Contacted</span>{' '}
              <span className="hg-chip reminder">Reminder sent</span>{' '}
              <span className="hg-chip replied">In talks</span>{' '}
              <span className="hg-chip confirmed">Signed up</span>{' '}
              <span className="hg-chip declined">Not joining</span>
            </p>
            <ul>
              <li><strong>Signed up</strong> is set by the brand itself when it books — you don't have to do anything. The <strong>Booked</strong> column shows what they took.</li>
              <li><strong>In talks</strong> — they replied and you're discussing.</li>
              <li><strong>Not joining</strong> — they said no (or you decided not to ask). It never removes the brand from the campaign or its placements.</li>
            </ul>
            <h3>Per-row actions</h3>
            <dl className="hg-defs">
              <dt>✉ Compose email</dt><dd>Opens a draft in your mail client, pre-filled with every brand contact, the team on CC, and the outreach text with the brand's personal booking link.</dd>
              <dt>✓ Confirmation email</dt><dd>Once they've signed up: a draft that sums up their slots and total.</dd>
              <dt>🔗 Copy link</dt><dd>Copies the brand's personal link — for pasting into an existing thread.</dd>
            </dl>
            <div className="hg-callout warn">
              <span className="hg-cl-label">Watch out</span>
              Booking only works through a brand's <strong>personal link</strong> — that's how mosy knows who booked. Never send the plain page address.
            </div>
          </section>

          <section id="public-page">
            <div className="hg-section-head"><span className="hg-section-num">09</span><h2>What brands see</h2></div>
            <p className="hg-lede">The booking page — where every outreach email points. Same page, a personal link per brand, in English, Dutch or French (picked from the brand's country; they can switch at the top).</p>
            <h3>Booking in three steps</h3>
            <ol>
              <li><strong>Where do you want to appear?</strong> Tabs per website page (with the frames on the screenshots) and per off-site placement (newsletter, social pack — shown with the mockups).</li>
              <li><strong>Pick your days</strong> — a day filter, then the slots with their price and how many are left. Highlighted slots stand out.</li>
              <li><strong>Sign up</strong> — a slim bar at the bottom of the screen leads to their selection (each line has a ✕ to remove it); they see their selection and total, set their customer discount (only when the page asks for a minimum discount), accept the terms and sign up.</li>
            </ol>
            <p>Signing up is final. The row turns <span className="hg-chip confirmed">Signed up</span>, the brand is tagged on each booked placement (and on the story and reel of a Social pack), and the slots count as taken for everyone else.</p>
            <div className="hg-callout tip">
              <span className="hg-cl-label">First come, first served</span>
              If two brands go for the last slot at the same time, the first sign-up wins; for the other brand that slot drops out of their selection with a message, and they can pick another one.
            </div>
          </section>

          <section id="results">
            <div className="hg-section-head"><span className="hg-section-num">10</span><h2>Fill in results</h2></div>
            <p className="hg-lede">Once the campaign has run, the Results tab is where you log what happened. <em>For now it only shows on old pack pages — on booking pages it's hidden until we start logging results.</em></p>
            <h3>Per-placement metrics</h3>
            <p>Brand filter at the top narrows to just the placements involving one partner. For each placement, you enter raw numbers copied from Klaviyo (newsletters) or Meta Business Suite (Instagram):</p>
            <ul>
              <li><strong>Newsletter</strong>: Recipients, Opens, Clicks → open rate and CTR auto-compute.</li>
              <li><strong>Instagram</strong>: Reach, Impressions, Engagement → engagement rate auto-computes.</li>
            </ul>
            <p>Save per placement (small button on each card). A green "Saved" pill confirms.</p>
            <div className="hg-callout tip">
              <span className="hg-cl-label">Who shows up in the filter</span>
              Only brands who pay something (anything over €0). Free participants don't clutter the list.
            </div>
          </section>

          <section id="share-results">
            <div className="hg-section-head"><span className="hg-section-num">11</span><h2>Share the recap</h2></div>
            <p className="hg-lede">Two ways to send results back to the brand: a PDF lookbook or a live web page.</p>
            <dl className="hg-defs">
              <dt>Open partner PDF</dt><dd>Opens the campaign lookbook filtered to that one brand — hero, "by the numbers" page, per-category asset gallery with proofs and metrics. Downloadable as PDF.</dd>
              <dt>Copy share link</dt><dd>Copies a public URL styled like the partnership page. Brand-specific hero ("Here's how X performed for Brand"), aggregated totals, per-placement cards with metrics. Uses the same unique token the brand already got at outreach — no new links to manage.</dd>
            </dl>
          </section>

          <section id="billing">
            <div className="hg-section-head"><span className="hg-section-num">12</span><h2>Invoice partners</h2></div>
            <p className="hg-lede">The Billing tab is a one-page overview of who you need to invoice in Odoo.</p>
            <ul>
              <li>Rows: every <span className="hg-chip confirmed">Signed up</span> brand.</li>
              <li>€0 rows are hidden — free participants don't get an invoice.</li>
              <li>Columns: brand, contact, discount, amount (what they booked). Total at the bottom.</li>
              <li><strong>Download CSV</strong> — opens directly in Excel, total row included.</li>
              <li><strong>Print / save as PDF</strong> — opens a clean printable one-pager; use the browser's "Save as PDF" option.</li>
            </ul>
          </section>

          <section id="attachments">
            <div className="hg-section-head"><span className="hg-section-num">13</span><h2>Files &amp; notes</h2></div>
            <p className="hg-lede">Every brand, campaign, placement and task has room for files and notes.</p>
            <ul>
              <li><strong>Attachments</strong> live under the entity they belong to. 25 MB per file. Drop them into the Attachments row, click to download, X to remove.</li>
              <li><strong>Notes</strong> are private working scratch — no one outside mosy sees them.</li>
              <li>On tasks specifically, you can queue attachments while creating the task; they upload after the task is saved.</li>
            </ul>
          </section>

          <section id="lookbooks">
            <div className="hg-section-head"><span className="hg-section-num">14</span><h2>Lookbooks</h2></div>
            <p className="hg-lede">Two flavours, same layout under the hood.</p>
            <dl className="hg-defs">
              <dt>Campaign lookbook</dt><dd>Every placement on a campaign, grouped by category with proofs and metrics. Downloadable PDF. Great for post-campaign internal review or agency handovers.</dd>
              <dt>Brand lookbook</dt><dd>Every placement across every campaign that featured a given brand — a running per-brand recap. Cover uses the brand name; subtitle is the year range. Perfect for annual reviews.</dd>
            </dl>
            <p>Both open from a small <strong>Lookbook</strong> button in the campaign / brand header. The per-brand PDF from the Results tab is just the campaign lookbook filtered to that one brand.</p>
          </section>

          <section id="shortcuts">
            <div className="hg-section-head"><span className="hg-section-num">15</span><h2>Small handy things</h2></div>
            <ul>
              <li><strong>← / →</strong> keys on any brand page — page through brands in the same order as the brands list.</li>
              <li><strong>Search bar</strong> at the top — reaches into brands, campaigns, placements and tasks.</li>
              <li><strong>Duplicate campaign</strong> — copies the placements + tasks to a new campaign with fresh dates.</li>
              <li>The chip colours are the same everywhere — <span className="hg-chip confirmed">Signed up</span> is always green, <span className="hg-chip declined">Declined</span> is always red. Keep an eye on them, they carry a lot.</li>
              <li>Every ✉ button in mosy respects your mail client — Superhuman, Gmail, Apple Mail. It just opens a draft you can review before sending.</li>
            </ul>
            <div className="hg-callout tip">
              <span className="hg-cl-label">Questions</span>
              Doreen is the go-to. If something feels off, chances are she's already thinking about it.
            </div>
          </section>

          <div className="hg-footer">
            <div><span className="yellow-dot"></span>Mosy · a LampTwist internal tool</div>
            <div>Keep this open in a tab</div>
          </div>
        </main>

        <aside className="hg-toc" aria-label="Table of contents">
          <div className="hg-toc-eyebrow">Getting started</div>
          <ul className="hg-toc-list">
            <li><a href="#intro"><span className="num">00</span><span>What mosy is</span></a></li>
            <li><a href="#getting-around"><span className="num">01</span><span>Getting around</span></a></li>
          </ul>
          <div className="hg-toc-eyebrow">Your ingredients</div>
          <ul className="hg-toc-list">
            <li><a href="#brands"><span className="num">02</span><span>Brands</span></a></li>
            <li><a href="#campaigns"><span className="num">03</span><span>Campaigns</span></a></li>
            <li><a href="#placements"><span className="num">04</span><span>Placements</span></a></li>
            <li><a href="#tasks"><span className="num">05</span><span>Tasks</span></a></li>
          </ul>
          <div className="hg-toc-eyebrow">Partnerships</div>
          <ul className="hg-toc-list">
            <li><a href="#placements-prices"><span className="num">06</span><span>Placements &amp; prices</span></a></li>
            <li><a href="#partnership-setup"><span className="num">07</span><span>Set up the booking page</span></a></li>
            <li><a href="#outreach"><span className="num">08</span><span>Reach out</span></a></li>
            <li><a href="#public-page"><span className="num">09</span><span>What brands see</span></a></li>
          </ul>
          <div className="hg-toc-eyebrow">After the campaign</div>
          <ul className="hg-toc-list">
            <li><a href="#results"><span className="num">10</span><span>Fill in results</span></a></li>
            <li><a href="#share-results"><span className="num">11</span><span>Share the recap</span></a></li>
            <li><a href="#billing"><span className="num">12</span><span>Invoice partners</span></a></li>
          </ul>
          <div className="hg-toc-eyebrow">Extras</div>
          <ul className="hg-toc-list">
            <li><a href="#attachments"><span className="num">13</span><span>Files & notes</span></a></li>
            <li><a href="#lookbooks"><span className="num">14</span><span>Lookbooks</span></a></li>
            <li><a href="#shortcuts"><span className="num">15</span><span>Small handy things</span></a></li>
          </ul>
        </aside>
      </div>
    </div>
  );
};

export default HowToGuide;
